if (!customElements.get('shihiko-header')) {
	class ShihikoHeader extends HTMLElement {
		connectedCallback() {
			this.abortController = new AbortController();
			this.header = this.querySelector('.header-new');
			this.collectionMenu = this.querySelector('.collection-menu');
			this.resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.syncHeaderMeasurements()) : null;
			this.lastScrollY = window.scrollY;
			this.scrollState = 'top';
			this.mobileMenu = this.querySelector('#header-mobile-menu.header-new__mobile-menu');
			this.menuButton = this.querySelector('.header-new__mobile-menu-button');
			this.currencies = this.querySelectorAll('.header-new__currency');
			this.desktopMenuItems = this.querySelectorAll('.header-new__desktop-bar .header-menu-item.has-submenu');
			this.mobileMenuItems = this.querySelectorAll('.header-new__mobile-menu .collapsible-block');
			this.searchTriggers = document.querySelectorAll('.js-header-search-trigger');
			this.cartTriggers = this.querySelectorAll('.js-cart-trigger-open');
			this.cartCloseTriggers = document.querySelectorAll('.cart-popup-overflow, #cart-popup .js-cart-trigger');
			this.pageHero = this.getPageHero();

			if (!this.header || !this.mobileMenu || !this.menuButton) return;

			const signal = this.abortController.signal;

			this.menuButton.addEventListener('click', () => this.setMobileMenuState(!this.mobileMenu.classList.contains('open')), { signal });

			this.searchTriggers.forEach((trigger) => {
				trigger.addEventListener('click', () => {
					this.setMobileMenuState(false);
					this.closeDesktopMenus();
				}, { signal });
			});

			this.desktopMenuItems.forEach((item) => {
				const link = item.querySelector('.header-new__nav-link');
				link?.addEventListener('click', (event) => this.onDesktopMenuClick(event, item), { signal });
			});

			this.mobileMenuItems.forEach((item) => {
				if (item.classList.contains('header-new__currency')) return;

				const button = item.querySelector('.header-new__mobile-link');
				button?.addEventListener('click', (event) => this.onMobileMenuClick(event, item), { signal });
			});

			this.currencies.forEach((currency) => {
				currency.querySelector('.currency-label')?.addEventListener('click', (event) => this.onCurrencyClick(event, currency), { signal });

				currency.querySelectorAll('input[name="currency"]').forEach((input) => {
					input.addEventListener('change', () => this.setCurrencyState(false), { signal });
				});
			});

			this.cartTriggers.forEach((trigger) => {
				trigger.addEventListener('click', (event) => this.onCartClick(event, trigger), { signal });
			});

			this.cartCloseTriggers.forEach((trigger) => {
				trigger.addEventListener('click', () => this.closeCartPopup(), { signal });
			});

			document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
			document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
			window.addEventListener('scroll', () => this.syncPageHeroContrast(), { passive: true, signal });
			window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
			window.addEventListener('resize', () => {
				this.pageHero = this.getPageHero();
				this.syncPageHeroContrast();
				this.syncMobileSubmenuHeights();
				this.scrollActiveCollectionMenuIntoView();
			}, { signal });
			this.syncPageHeroContrast();
			this.syncHeaderMeasurements();
			this.scrollActiveCollectionMenuIntoView();
			this.resizeObserver?.observe(this.header);
			if (this.collectionMenu) this.resizeObserver?.observe(this.collectionMenu);
		}

		disconnectedCallback() {
			this.abortController?.abort();
			this.resizeObserver?.disconnect();
		}

		getPageHero() {
			if (document.body.classList.contains('template-name-product') && window.matchMedia('(max-width: 1024px)').matches) {
				return document.querySelector('body.template-name-product .product-gallery');
			}

			return document.querySelector('body.template-name-index home-hero-carousel, body.template-name-collection .collection-hero, body.template-name-product .main > .shopify-section:first-child');
		}

		syncPageHeroContrast() {
			const hasOverlayHero = document.body.classList.contains('header-overlay-page');
			if (!hasOverlayHero) return;

			if (!this.pageHero || !this.header) {
				this.classList.add('header-new-past-hero');
				return;
			}

			const heroBottom = this.pageHero.getBoundingClientRect().bottom;
			const headerBottom = this.header.getBoundingClientRect().bottom;
			this.classList.toggle('header-new-past-hero', heroBottom <= headerBottom);
		}

		syncHeaderMeasurements() {
			if (!this.header) return;

			this.style.setProperty('--primary-header-height', `${this.header.offsetHeight}px`);
			this.style.setProperty('--collection-menu-height', `${this.collectionMenu?.offsetHeight || 0}px`);
			document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
		}

			scrollActiveCollectionMenuIntoView() {
				if (!this.collectionMenu || !window.matchMedia('(max-width: 1024px)').matches) return;

				const list = this.collectionMenu.querySelector('.collection-menu__list');
				const activeLink = list?.querySelector('.collection-menu__link--active');
				const activeItem = activeLink?.closest('.collection-menu__item');
				if (!list || !activeItem) return;

				window.requestAnimationFrame(() => {
					if (!this.isConnected) return;

					const listRect = list.getBoundingClientRect();
					const itemRect = activeItem.getBoundingClientRect();
					const targetScrollLeft = list.scrollLeft + itemRect.left - listRect.left;
					const maxScrollLeft = Math.max(list.scrollWidth - list.clientWidth, 0);

					list.scrollLeft = Math.min(Math.max(targetScrollLeft, 0), maxScrollLeft);
				});
			}

		isHeaderInteractionOpen() {
			return Boolean(
				this.mobileMenu?.classList.contains('open') ||
				this.classList.contains('header-new-search-expanded') ||
				this.querySelector('.header-new__desktop-bar .open-submenu') ||
				document.querySelector('#cart-popup.open')
			);
		}

		setScrollState(state) {
			if (this.scrollState === state) return;

			this.scrollState = state;
			this.classList.toggle('header-new-scroll-compact', state === 'compact');
			this.classList.toggle('header-new-scroll-reveal', state === 'reveal');
			window.requestAnimationFrame(() => this.syncHeaderMeasurements());
		}

		onScroll() {
			if (!this.collectionMenu || !document.body.classList.contains('header-overlay-page')) return;

			const currentScrollY = Math.max(window.scrollY, 0);
			const scrollDelta = currentScrollY - this.lastScrollY;
			this.lastScrollY = currentScrollY;

			if (currentScrollY <= 0) {
				this.setScrollState('top');
				return;
			}

			if (this.isHeaderInteractionOpen() || Math.abs(scrollDelta) < 4) return;

			if (scrollDelta > 0 && currentScrollY > this.header.offsetHeight) {
				this.setScrollState('compact');
			} else if (scrollDelta < 0) {
				this.setScrollState('reveal');
			}
		}

		setMobileMenuState(open) {
			if (open) this.setScrollState('reveal');
			this.setCurrencyState(false);
			this.mobileMenu?.classList.toggle('open', open);
			this.menuButton?.classList.toggle('open', open);
			this.classList.toggle('header-new-mobile-menu-open', open);
			this.menuButton?.setAttribute('aria-expanded', open ? 'true' : 'false');
			this.menuButton?.setAttribute('aria-label', open ? 'Close mobile menu' : 'Open mobile menu');
			document.body.classList.toggle('disable-scroll', open);
		}

		closeDesktopMenus(exceptItem = null) {
			this.desktopMenuItems.forEach((item) => {
				if (item !== exceptItem) {
					item.classList.remove('open-submenu');
					item.querySelector('.header-new__nav-link')?.setAttribute('aria-expanded', 'false');
				}
			});
		}

		onDesktopMenuClick(event, item) {
			event.preventDefault();
			event.stopPropagation();

			const shouldOpen = !item.classList.contains('open-submenu');
			this.closeDesktopMenus(item);
			item.classList.toggle('open-submenu', shouldOpen);
			item.querySelector('.header-new__nav-link')?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
		}

		onCurrencyClick(event, currency) {
			event.preventDefault();
			event.stopPropagation();
			this.setCurrencyState(!currency.classList.contains('open-submenu'), currency);
		}

		setCurrencyState(open, targetCurrency = null) {
			this.currencies.forEach((currency) => {
				const shouldOpen = Boolean(open && (!targetCurrency || currency === targetCurrency));
				currency.classList.toggle('open-submenu', shouldOpen);
				currency.querySelector('.currency-label')?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
			});
		}

		onCartClick(event, trigger) {
			event.preventDefault();
			this.setMobileMenuState(false);
			this.closeDesktopMenus();
			this.setCurrencyState(false);
			this.loadCartPopup(trigger);
			this.openCartPopup();
		}

		loadCartPopup(trigger) {
			if (trigger?.dataset.loaded === 'true') return;
			if (trigger) {
				trigger.dataset.loaded = 'true';
			}

			if (typeof window.updateCartPopup === 'function') {
				window.updateCartPopup();
				return;
			}

			const cartPopup = document.querySelector('#cart-popup');
			if (!cartPopup || !window.routes?.cart_url) return;

			cartPopup.classList.add('loading');
			fetch(`${window.routes.cart_url}?section_id=ajax-cart-popup`)
				.then((response) => response.text())
				.then((responseText) => {
					const html = new DOMParser().parseFromString(responseText, 'text/html');
					const fetchCartPopup = html.querySelector('#cart-popup');
					if (fetchCartPopup) {
						cartPopup.innerHTML = fetchCartPopup.innerHTML;
					}
				})
				.catch((error) => {
					console.error(error);
				})
				.finally(() => {
					cartPopup.classList.remove('loading');
					this.bindCartPopupCloseTriggers();
				});
		}

		bindCartPopupCloseTriggers() {
			const signal = this.abortController?.signal;
			document.querySelectorAll('#cart-popup .js-cart-trigger').forEach((trigger) => {
				trigger.addEventListener('click', () => this.closeCartPopup(), { signal });
			});
		}

		openCartPopup() {
			document.querySelector('#cart-popup')?.classList.add('open');
			document.querySelector('.cart-popup-overflow')?.classList.add('open');
			document.body.classList.add('disable-scroll');
		}

		closeCartPopup() {
			document.querySelector('#cart-popup')?.classList.remove('open');
			document.querySelector('.cart-popup-overflow')?.classList.remove('open');
			document.body.classList.remove('disable-scroll');
		}

		onMobileMenuClick(event, item) {
			event.preventDefault();
			event.stopPropagation();

			const shouldOpen = !item.classList.contains('open');
			this.setMobileSubmenuState(item, shouldOpen);
		}

		setMobileSubmenuState(item, open) {
			const content = item.querySelector('.collapsible-content');
			const button = item.querySelector('.header-new__mobile-link');

			item.classList.toggle('open', open);
			button?.setAttribute('aria-expanded', open ? 'true' : 'false');

			if (!content) return;

			if (open) {
				item.classList.add('open');
				content.style.height = 'auto';
				const contentHeight = content.scrollHeight;
				content.style.height = '0';
				window.requestAnimationFrame(() => {
					content.style.height = `${contentHeight}px`;
				});
				return;
			}

			content.style.height = `${content.scrollHeight}px`;
			window.requestAnimationFrame(() => {
				content.style.height = '0';
			});
		}

		syncMobileSubmenuHeights() {
			this.mobileMenuItems.forEach((item) => {
				if (!item.classList.contains('open')) return;
				const content = item.querySelector('.collapsible-content');
				if (content) content.style.height = `${content.scrollHeight}px`;
			});
		}

		onKeydown(event) {
			if (event.key !== 'Escape') return;

			this.setMobileMenuState(false);
			this.setCurrencyState(false);
			this.closeCartPopup();
			this.closeDesktopMenus();
		}

		onDocumentClick(event) {
			if (!(event.target instanceof Element)) return;

			if (!event.target.closest('shihiko-header')) {
				this.closeDesktopMenus();
			}

			if (!event.target.closest('.header-new__currency')) {
				this.setCurrencyState(false);
			}
		}
	}

	customElements.define('shihiko-header', ShihikoHeader);
}
